import axios from 'axios';

import {addError, refreshToken} from 'state/actions';
import {getToken} from 'state/selectors';

const OAUTH_ERROR_MESSAGES = {
  INVALID_GRANT: 'invalid_grant',
  INVALID_TOKEN: 'invalid_token',
};

const httpClient = axios.create({
  // By default the errors thrown by the backend are stored in redux and displayed by a generic ErrorContainer component.
  // If a component wishes to handle/display errors in a different way, it shall set handleErrorsCentrally to false
  // when sending an HTTP request and implement the logic to handle/show the errors.
  handleErrorsCentrally: true,
});

const failedRequestsDuringRefresh = [];

function processPendingRequests(err) {
  for (const {resolve, reject} of failedRequestsDuringRefresh) {
    if (err) {
      reject(err);
    } else {
      resolve();
    }
  }

  failedRequestsDuringRefresh.splice(0, failedRequestsDuringRefresh.length);
}

async function handleExpiredToken(store, originalRequest) {
  try {
    // don't send multiple parallel requests to refresh the token
    if (store.getState().auth.isRefreshing) {
      return new Promise((resolve, reject) => {
        failedRequestsDuringRefresh.push({resolve, reject});
      }).then(() => {
        originalRequest._retried = true;
        return httpClient(originalRequest);
      });
    } else {
      await store.dispatch(refreshToken());

      originalRequest._retried = true;
      return httpClient(originalRequest).then(data => {
        // resolve all pending Promises; this will end up retrying all failed requests
        processPendingRequests();
        return data;
      });
    }
  } catch (ex) {
    // reject all failed requests
    processPendingRequests(ex);
    return Promise.reject(ex);
  }
}

export function attachInterceptors(store) {
  httpClient.interceptors.request.use(config => {
    // we only want to attach the bearer token to requests to /api/* or /refresh endpoints
    if (
      (!config.url.startsWith('/api') && config.url !== '/refresh') ||
      config.url === '/api/config'
    ) {
      return config;
    }

    // attach the token to each request to the /api endpoint
    const token = getToken(store.getState());

    config.headers['Accept'] = 'application/json';
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    return config;
  });

  httpClient.interceptors.response.use(
    response => response,
    async responseErr => {
      // !responseErr.response makes sure that requests aborted by the browser are handled properly
      if (axios.isCancel(responseErr) || !responseErr.response) {
        return Promise.reject(responseErr);
      }

      // retry the request if it failed due to expired token
      const originalRequest = responseErr.config;
      const {status, data} = responseErr.response;

      let error, errors;
      if (data instanceof Blob) {
        const jsonResponse = JSON.parse(await data.text());
        error = jsonResponse.error;
        errors = jsonResponse.errors;
      } else {
        error = data.error;
        errors = data.errors;
      }

      if (status === 401 && !originalRequest._retried) {
        if (error === OAUTH_ERROR_MESSAGES.INVALID_TOKEN) {
          // we try to refresh the token and retry all failed requests
          return handleExpiredToken(store, originalRequest);
        } else if (error === OAUTH_ERROR_MESSAGES.INVALID_GRANT) {
          // refresh token has expired or is invalid thus we need to reauthenticate the user
          localStorage.removeItem('token');
          localStorage.setItem(
            'location',
            window.location.href.replace(window.location.origin, '')
          );

          return Promise.reject(responseErr).finally(() => {
            store.dispatch(
              addError('Your session has ended and you will be now forced to reauthenticate')
            );
            window.location.replace('/login');
          });
        } else {
          // needed for other cases, e.g. token_missing,
          // for the moment forward to the originating component to handle the error
          // later treat the same as the case below with a TODO
          return Promise.reject(responseErr).finally(() => {
            store.dispatch(addError(error));
          });
        }
      }

      if (responseErr.config.handleErrorsCentrally) {
        if (error) {
          store.dispatch(addError(error));
        }

        if (Array.isArray(errors) && errors.length > 0) {
          errors.forEach(_error => {
            store.dispatch(addError(_error));
          });
        }
      }

      // the originating component should also handle the error thus we need to reraise it
      return Promise.reject(responseErr);
    }
  );
}

export default httpClient;
